/**
 * MedListItemsEditor: the medication list helper's rows, one card per
 * medicine, in the record form's words and order (Medication name, Dose,
 * When, Directions, Status, Prescriber, Reason or condition), plus How it
 * is taken (the printed list's Method column, which the record does not
 * keep). When is the record form's five ticks (TickGroup); with a client
 * linked, Prescriber is the record form's care-team picker over that
 * client's care team and the Directory, else a plain box.
 *
 * Two fields share a line only when the card is at least 28rem wide (a
 * container query in rem), so larger text stacks them rather than
 * squeezing them.
 *
 * The rows only make a document: nothing here writes the record. The
 * prescriber prints in the list's Prescribed by column (LP-32). Shared by
 * the tool page and the record's "Make med list" dialog through
 * MedListReview.
 */

import { type ReactNode, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { SelectField } from '@/components/primitives/SelectField';
import { TextInput } from '@/components/primitives/TextInput';
import { TickGroup } from '@/components/primitives/TickGroup';
import { FLD } from '@/components/primitives/modalFormStyles';
import { CareTeamCombobox, type CareTeamPick } from '@/components/clientRecord/CareTeamCombobox';
import { recordFieldLabel, recordFieldHelp, valueLabel } from '@/lib/recordFormWords';
import { textBoxProps } from '@/lib/textBoxProps';
import { MED_LIST_BUCKETS, emptyMedListItem, type MedListItem } from '@/lib/medList';
import type { ClientPronouns, ProviderRow } from '@/lib/clientRecord';

/** The printed list's Method column, which the record form has no field for. */
export const MED_LIST_METHOD_LABEL = 'How it is taken';

const STATUSES: MedListItem['status'][] = ['active', 'discontinued'];

export interface MedListCareTeam {
  providers: readonly ProviderRow[];
  pronouns: ClientPronouns | null;
}

export interface MedListItemsEditorProps {
  items: readonly MedListItem[];
  onChange: (items: MedListItem[]) => void;
  /** The linked client's care team, for the Prescriber picker; absent
   *  when the list is not linked (a plain box). */
  careTeam?: MedListCareTeam | null;
}

const label = (field: string): string => recordFieldLabel('medications', field);

export function MedListItemsEditor({ items, onChange, careTeam = null }: MedListItemsEditorProps): ReactNode {
  // A picker's pick per row, so its "On the care team" line holds; the
  // document keeps the name only.
  const [picks, setPicks] = useState<Record<number, CareTeamPick | null>>({});

  const set = (i: number, patch: Partial<MedListItem>): void => {
    onChange(items.map((item, j) => (j === i ? { ...item, ...patch } : item)));
  };
  const remove = (i: number): void => {
    onChange(items.filter((_, j) => j !== i));
    setPicks({});
  };

  return (
    <div className="space-y-3" data-testid="med-list-items">
      {items.map((item, i) => {
        const id = `ml-item-${i}`;
        const tid = `med-list-item-${i}`;
        const text = (field: 'name' | 'dose' | 'directions' | 'reason' | 'method', words: string): ReactNode => (
          <Field
            label={words}
            htmlFor={`${id}-${field}`}
            required={field === 'name'}
            help={field === 'directions' ? recordFieldHelp('medications', 'schedule') : undefined}
          >
            <TextInput
              id={`${id}-${field}`}
              data-testid={`${tid}-${field}`}
              className={`${FLD} h-9 px-2 !text-sm`}
              value={item[field]}
              onChange={(e) => set(i, { [field]: e.target.value })}
              {...textBoxProps(field === 'reason' ? 'medication_reason' : 'medication')}
            />
          </Field>
        );
        return (
          <section
            key={i}
            className="@container rounded-[12px] border border-sage-800/[8%] bg-paper p-3.5 space-y-3"
            data-testid={tid}
            aria-label={item.name.trim() !== '' ? item.name : 'New medication'}
          >
            <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
              {text('name', label('name'))}
              {text('dose', label('dose'))}
            </div>
            <TickGroup
              id={`${id}-times`}
              legend={label('times_of_day')}
              options={MED_LIST_BUCKETS.map((k) => ({ value: k, label: valueLabel('medications', 'times_of_day', k) }))}
              value={item.times}
              onChange={(next) => set(i, { times: MED_LIST_BUCKETS.filter((k) => next.includes(k)) })}
              testIdPrefix={`${tid}-times`}
            />
            {text('directions', label('schedule'))}
            <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
              <Field label={label('status')} htmlFor={`${id}-status`}>
                <SelectField
                  size="default"
                  id={`${id}-status`}
                  data-testid={`${tid}-status`}
                  className="w-full"
                  value={item.status}
                  onChange={(e) => set(i, { status: e.target.value as MedListItem['status'] })}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {valueLabel('medications', 'status', s)}
                    </option>
                  ))}
                </SelectField>
              </Field>
              {text('method', MED_LIST_METHOD_LABEL)}
            </div>
            <Field label={label('prescriber')} htmlFor={`${id}-prescriber`} help="Prints beside the medication, in the Prescribed by column.">
              {careTeam !== null ? (
                <CareTeamCombobox
                  id={`${id}-prescriber`}
                  value={item.prescriber}
                  pick={picks[i] ?? null}
                  onChange={(next) => {
                    setPicks((prev) => ({ ...prev, [i]: next.pick }));
                    set(i, { prescriber: next.value });
                  }}
                  careTeam={careTeam.providers}
                  pronouns={careTeam.pronouns}
                  listLabel="Prescriber choices"
                  testIdPrefix={`${tid}-prescriber`}
                />
              ) : (
                <TextInput
                  id={`${id}-prescriber`}
                  data-testid={`${tid}-prescriber`}
                  className={`${FLD} h-9 px-2 !text-sm`}
                  value={item.prescriber}
                  onChange={(e) => set(i, { prescriber: e.target.value })}
                  {...textBoxProps('person')}
                />
              )}
            </Field>
            {text('reason', label('reason'))}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                icon={<Trash2 size={15} aria-hidden="true" />}
                onClick={() => remove(i)}
                data-testid={`${tid}-remove`}
              >
                Remove
              </Button>
            </div>
          </section>
        );
      })}
      <Button
        variant="secondary"
        size="sm"
        icon={<Plus size={15} aria-hidden="true" />}
        onClick={() => onChange([...items, emptyMedListItem()])}
        data-testid="med-list-item-add"
      >
        Add a medication
      </Button>
    </div>
  );
}
